import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import type { CatalogueItemData, InventoryStoreRequest } from '@jobilla/nexus-sdk'
import type { AxiosError } from 'axios'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountField, Button, DateField } from '../../../../rapid-ui'
import { DescriptionCombobox } from './DescriptionCombobox'

interface CreateInventoryDialogProps {
	open: boolean
	onClose: () => void
	onSubmit: (data: InventoryStoreRequest) => void
	catalogueItems: CatalogueItemData[]
	isLoading: boolean
	error: AxiosError<{ message?: string }> | null
}

export function CreateInventoryDialog({
	open,
	onClose,
	onSubmit,
	catalogueItems,
	isLoading,
	error,
}: CreateInventoryDialogProps) {
	const { t, i18n } = useTranslation('common')
	const [description, setDescription] = useState('')
	const [valueEuros, setValueEuros] = useState(0)
	const [quantity, setQuantity] = useState(1)
	const [expiresAt, setExpiresAt] = useState(() => {
		const date = new Date()
		date.setFullYear(date.getFullYear() + 1)
		return date
	})

	// A description that doesn't match a preset has no catalogue record, so it falls
	// back to the campaign-budget catalogue item to satisfy the required catalogue_item_id.
	const fallbackCatalogueItem = useMemo(
		() => catalogueItems.find((item) => item.type === 'campaign_budget') ?? catalogueItems[0] ?? null,
		[catalogueItems],
	)

	const handleSubmit = () => {
		const matchedItem = catalogueItems.find((item) => item.name === description.trim())
		const catalogueItem = matchedItem ?? fallbackCatalogueItem
		if (!catalogueItem) { return }

		onSubmit({
			catalogueItemId: catalogueItem.id,
			valueCents: Math.round(valueEuros * 100),
			quantity,
			description,
			expiresAt: expiresAt.toISOString(),
		})
	}

	const handleClose = () => {
		setDescription('')
		setValueEuros(0)
		setQuantity(1)
		setExpiresAt(() => {
			const date = new Date()
			date.setFullYear(date.getFullYear() + 1)
			return date
		})
		onClose()
	}

	const isValid = fallbackCatalogueItem !== null
		&& description.trim() !== ''
		&& valueEuros > 0
		&& quantity >= 1

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={handleClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden"
						>
							<div className="p-6">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{t('inventory.create.title')}
								</DialogTitle>

								<div className="mt-4 space-y-4">
									<DescriptionCombobox
										label={t('inventory.create.description')}
										value={description}
										onChange={setDescription}
										options={catalogueItems.map((item) => item.name)}
										placeholder={t('inventory.create.descriptionPlaceholder')}
									/>

									<div className="flex items-start gap-3">
										<div className="flex-1 min-w-0">
											<AmountField
												label={t('inventory.create.valueEur')}
												value={valueEuros}
												onChange={setValueEuros}
												locale={i18n.language}
												prefix="€"
												suffix="+ VAT"
												placeholder="0.00"
											/>
										</div>
										<div className="text-2xl text-gray-400 mt-9">&times;</div>
										<div className="w-24 shrink-0">
											<AmountField
												label={t('inventory.create.quantity')}
												value={quantity}
												onChange={setQuantity}
												locale={i18n.language}
												integer
												placeholder="1"
											/>
										</div>
									</div>
									{valueEuros > 0 && quantity > 0 && (
										<div className="text-sm text-grey-700 -mt-2">
											{t('inventory.create.totalHint', {
												amount: new Intl.NumberFormat(i18n.language, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												}).format(valueEuros * quantity),
											})}
										</div>
									)}

									<DateField
										label={t('inventory.create.expiresAt')}
										value={expiresAt}
										onChange={setExpiresAt}
										minDate={new Date()}
									/>

									{error && (
										<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
											<div className="font-medium text-red-900">
												{t('inventory.create.errorCreate')}
											</div>
											<div className="text-red-700 mt-0.5">
												{error.response?.data?.message || error.message || t('common.tryAgain')}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
								<Button type="subtle" onClick={handleClose} disabled={isLoading}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									onClick={handleSubmit}
									isLoading={isLoading}
									disabled={!isValid}
								>
									{isLoading ? t('inventory.create.creating') : t('inventory.create.createItem')}
								</Button>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
